import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "../utils";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";

const firmApi = createApi({
  reducerPath: "firm",
  tagTypes: ["Sync", "Device", "Firm", "Bills", "RoleTemplate", "EmployeeTemplate", "FirmBillingSummary", "FirmConsumptionSummary"],
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  keepUnusedDataFor: 120,
  endpoints(builder) {
    return {
      addFirm: builder.mutation({
        query: (data) => apiFunction(data, `/api/firms/storageRoles/createFirm`, "POST"),
        invalidatesTags: ["Firm"],
      }),
      // Firma oluşturma sihirbazının ürün adımı (#275)
      getCreateFirmProductOptions: builder.query({
        query: (token) =>
          apiFunction({ token }, `/api/firms/storageRoles/createFirmProductOptions`, "POST"),
        keepUnusedDataFor: 300,
      }),
      updateFirmInfo: builder.mutation({
        query: (data) => apiFunction(data, `/api/firms/storageRoles/updateFirm`, "PATCH"),
        invalidatesTags: ["Firm"],
      }),
      updateFirmSync: builder.mutation({
        query: (data) => apiFunction(data, `/api/firms/storageRoles/updateSync`, "PATCH"),
        invalidatesTags: ["Sync"],
      }),
      updateFirmProductQuota: builder.mutation({
        query: (input) => {
          return {
            url: `/api/firms/storageRoles/updateFirmProductQuota/${input.id}`,
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${input.token}`,
            },
            body: {
              ...input,
            },
          };
        },
      }),
      updateTakeBackFirmProductQuota: builder.mutation({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/takeBackFirmProductQuota`, "PATCH"),
        invalidatesTags: (result, error, arg) => [
          "Bills",
          { type: "FirmBillingSummary", id: arg?.id },
          { type: "FirmConsumptionSummary", id: arg?.id },
        ],
      }),
      updateGiveFirmProductQuota: builder.mutation({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/giveFirmProductQuota`, "PATCH"),
        invalidatesTags: (result, error, arg) => [
          "Bills",
          { type: "FirmBillingSummary", id: arg?.id },
          { type: "FirmConsumptionSummary", id: arg?.id },
        ],
      }),
      // ALT FIRMAYA KOTA GONDER — "Kota Paylasimi".
      // Iki firmanin da havuzu ve faturasi degistigi icin Firm/Bills
      // etiketlerinin TAMAMI gecersizlenir (yalnizca gonderen firmanin
      // ozetini gecersizlemek alt firmanin ekranini bayat birakirdi).
      sendSubFirmQuota: builder.mutation({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/sendSubFirmQuota`, "PATCH"),
        invalidatesTags: (result, error, arg) => [
          "Firm",
          "Bills",
          { type: "FirmBillingSummary", id: arg?.id },
          { type: "FirmBillingSummary", id: arg?.subFirmId },
          { type: "FirmConsumptionSummary", id: arg?.id },
          { type: "FirmConsumptionSummary", id: arg?.subFirmId },
        ],
      }),
      // Gonderen firmanin urun bazinda gonderilebilir (dagitilmamis) kotasi
      getSendableSubFirmQuota: builder.query({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/sendableSubFirmQuota`, "POST"),
        providesTags: ["Firm"],
      }),
      divideQuota: builder.mutation({
        query: (input) => {
          return {
            url: `/api/firms/storageRoles/divideQuota/${input.id}`,
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${input.token}`,
            },
            body: {
              ...input,
            },
          };
        },
      }),
      getSyncByFirmID: builder.query({
        query: (data) => apiFunction(data, `/api/firms/storageRoles/getFirmSync`, "POST"),
      }),
      getDeviceByFirmID: builder.query({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/getDevicesOfFirm`, "POST"),
      }),
      // Firma ürününü tamamen sil — sadece admin, 2FA kodu (code) ile
      deleteFirmProduct: builder.mutation({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/deleteFirmProduct`, "DELETE"),
        invalidatesTags: ["Firm"],
      }),
      distributeFirmQuota: builder.mutation({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/distributeFirmQuota`, "POST"),
      }),
      // Kapsamlı Kota Dağıt (#386): { firmId, firmIds? } — her firma kendi havuzunu
      distributeFirmQuotas: builder.mutation({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/distributeFirmQuotas`, "POST"),
      }),
      deleteFirmByID: builder.mutation({
        query: (data) => apiFunction(data, `/api/firms/storageRoles/deleteFirm`, "DELETE"),
        invalidatesTags: ["Firm"],
      }),
      getFirmByID: builder.query({
        query: (data) => apiFunction(data, `/api/firms/storageRoles/getFirm`, "POST"),
        providesTags: ["Firm"],
      }),
      getOtherFirms: builder.query({
        query: ({ id, token }) => {
          return {
            url: `/api/firms/storageRoles/otherFirms/${id}`,
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
        },
        providesTags: ["Firm"],
      }),
      getFirmProducts: builder.query({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/getFirmProducts`, "POST"),
        providesTags: ["Firm"],
      }),
      getBelongUsersByFirmID: builder.query({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/belongUsersOfFirm`, "POST"),
        providesTags: ["Firm"],
      }),
      getBelowFirmsByID: builder.query({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/belowFirmsOfMainFirm`, "POST"),
        providesTags: ["Firm"],
      }),
      fetchStorageFirm: builder.query({
        query: (token) => {
          return {
            url: "/api/firms/storageRoles/getStorageFirms",
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
        },
        providesTags: ["Firm"],
      }),
      getFirm: builder.query({
        query: (data) => apiFunction(data, "/api/firms/storageRoles/getFirms", "POST"),
        providesTags: ["Firm"],
      }),
      searchFirmsForDropdown: builder.query({
        query: (data) => apiFunction(data, "/api/firms/storageRoles/searchFirmsForDropdown", "POST"),
      }),
      getCustomersByDealerFirmId: builder.query({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/getCustomersByDealerFirmId`, "POST"),
        providesTags: ["Firm"],
      }),
      updateFirmDealerRelation: builder.mutation({
        query: (data) =>
          apiFunction(data, `/api/firms/storageRoles/updateFirmDealerRelation`, "PATCH"),
        invalidatesTags: ["Firm"],
      }),
      // Role Template endpoints
      getRoleTemplates: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/roleTemplates/getRoleTemplates", "POST"),
        providesTags: ["RoleTemplate"],
        keepUnusedDataFor: 0,
      }),
      createRoleTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/roleTemplates/createRoleTemplate", "POST"),
        invalidatesTags: ["RoleTemplate"],
      }),
      updateRoleTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/roleTemplates/updateRoleTemplate", "PATCH"),
        invalidatesTags: ["RoleTemplate"],
      }),
      deleteRoleTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/roleTemplates/deleteRoleTemplate", "DELETE"),
        invalidatesTags: ["RoleTemplate"],
      }),
      getTemplateAvailablePermissions: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/roleTemplates/getAvailablePermissions", "POST"),
      }),
      // Employee Template endpoints
      getEmployeeTemplates: builder.query({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/employeeTemplates/getEmployeeTemplates", "POST"),
        providesTags: ["EmployeeTemplate"],
        keepUnusedDataFor: 0,
      }),
      createEmployeeTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/employeeTemplates/createEmployeeTemplate", "POST"),
        invalidatesTags: ["EmployeeTemplate"],
      }),
      updateEmployeeTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/employeeTemplates/updateEmployeeTemplate", "PATCH"),
        invalidatesTags: ["EmployeeTemplate"],
      }),
      deleteEmployeeTemplate: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/employeeTemplates/deleteEmployeeTemplate", "DELETE"),
        invalidatesTags: ["EmployeeTemplate"],
      }),
      // UserCreateDrawer addUser ile aynı imzayla çağırır: { input, token }.
      // apiFunction yalnız token'ı ayırır — input açılmazsa body { input: {...} }
      // gider ve sunucu "name zorunludur" ile 400 döner.
      createFirmUser: builder.mutation({
        query: ({ input, token }) =>
          apiFunction({ ...input, token }, `/api/firms/storageRoles/createFirmUser`, "POST"),
        invalidatesTags: ["Firm"],
      }),
      // SubFirm endpoints
      searchUnlinkedFirms: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/subfirms/searchUnlinkedFirms", "POST"),
      }),
      linkSubFirm: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/subfirms/linkSubFirm", "POST"),
        invalidatesTags: ["Firm"],
      }),
      unlinkSubFirm: builder.mutation({
        query: (data) =>
          apiFunction(data, "/api/firms/storageRoles/subfirms/unlinkSubFirm", "POST"),
        invalidatesTags: ["Firm"],
      }),
      getFirmBillingSummary: builder.query({
        query: (data) => apiFunction(data, `/api/firms/storageRoles/getFirmBillingSummary`, "POST"),
        providesTags: (result, error, arg) => [{ type: "FirmBillingSummary", id: arg?.firmId }],
        keepUnusedDataFor: 60,
      }),
      getFirmConsumptionSummary: builder.query({
        query: (data) => apiFunction(data, `/api/firms/storageRoles/getFirmConsumptionSummary`, "POST"),
        providesTags: (result, error, arg) => [{ type: "FirmConsumptionSummary", id: arg?.firmId }],
        keepUnusedDataFor: 60,
      }),
    };
  },
});

export const {
  useLazyGetFirmQuery,
  useGetFirmQuery,
  useFetchStorageFirmQuery,
  useAddFirmMutation,
  useGetFirmByIDQuery,
  useLazyGetFirmByIDQuery,
  useGetBelowFirmsByIDQuery,
  useUpdateFirmInfoMutation,
  useUpdateFirmSyncMutation,
  useUpdateFirmProductQuotaMutation,
  useUpdateTakeBackFirmProductQuotaMutation,
  useUpdateGiveFirmProductQuotaMutation,
  useSendSubFirmQuotaMutation,
  useGetSendableSubFirmQuotaQuery,
  useLazyGetSendableSubFirmQuotaQuery,
  useDeleteFirmByIDMutation,
  useGetBelongUsersByFirmIDQuery,
  useLazyGetDeviceByFirmIDQuery,
  useGetDeviceByFirmIDQuery,
  useGetSyncByFirmIDQuery,
  useLazyGetSyncByFirmIDQuery,
  useDivideQuotaMutation,
  useGetOtherFirmsQuery,
  useGetFirmProductsQuery,
  useLazyGetFirmProductsQuery,
  useDistributeFirmQuotaMutation,
  useDistributeFirmQuotasMutation,
  useDeleteFirmProductMutation,
  useGetCustomersByDealerFirmIdQuery,
  useUpdateFirmDealerRelationMutation,
  useLazySearchFirmsForDropdownQuery,
  useGetRoleTemplatesQuery,
  useLazyGetRoleTemplatesQuery,
  useCreateRoleTemplateMutation,
  useUpdateRoleTemplateMutation,
  useDeleteRoleTemplateMutation,
  useGetTemplateAvailablePermissionsQuery,
  useSearchUnlinkedFirmsMutation,
  useLinkSubFirmMutation,
  useUnlinkSubFirmMutation,
  useGetEmployeeTemplatesQuery,
  useLazyGetEmployeeTemplatesQuery,
  useCreateEmployeeTemplateMutation,
  useUpdateEmployeeTemplateMutation,
  useDeleteEmployeeTemplateMutation,
  useCreateFirmUserMutation,
  useGetFirmBillingSummaryQuery,
  useGetFirmConsumptionSummaryQuery,
  useGetCreateFirmProductOptionsQuery,
} = firmApi;
export { firmApi };
